import * as THREE from 'three';
import { PointShape } from '../defines';
import { NormalizationEDLMaterial } from '../materials/NormalizationEDLMaterial';
import { NormalizationMaterial } from '../materials/NormalizationMaterial';
import { PointCloudMaterial } from '../materials/PointCloudMaterial';
import { Utils } from '../utils';
import type { RenderPipeline, RenderPipelineContext, RenderPipelineState } from './RenderPipeline';

/**
 * Runtime point cloud material with all dynamic properties.
 * PointCloudMaterial sets many properties in its constructor without class declarations.
 * This interface covers the properties used by HQSplatRenderer.
 */
interface RuntimePCMaterial {
  size: number;
  minSize: number;
  maxSize?: number;
  pointSizeType: number;
  activeAttributeName: string | null;
  visibleNodesTexture: THREE.Texture;
  weighted: boolean;
  screenWidth: number;
  screenHeight: number;
  shape: number;
  spacing: number;
  classification: unknown;
  classificationTexture: { needsUpdate: boolean };
  clipBoxes: unknown[];
  clipPolygons: unknown[];
  useEDL: boolean;
  useLogarithmicDepthBuffer: boolean;
  elevationGradientRepat: number;
  elevationRange: number[];
  gradient: unknown;
  matcap: string;
  intensityRange: number[];
  intensityGamma: number;
  intensityContrast: number;
  intensityBrightness: number;
  rgbGamma: number;
  rgbContrast: number;
  rgbBrightness: number;
  weightRGB: number;
  weightIntensity: number;
  weightElevation: number;
  weightClassification: number;
  weightReturnNumber: number;
  weightSourceID: number;
  color: THREE.Color;
  uniforms: Record<string, { value: unknown }>;
  setClipBoxes(boxes: unknown[]): void;
  setClipPolygons(polygons: unknown[], maxVertices?: number): void;
  setDefine(key: string, value: string): void;
}

/**
 * Per-view state for the HQ splat pipeline. Each RenderView owns its own RTs,
 * normalization materials, and per-pointcloud depth/attribute material caches.
 *
 * The material caches are keyed by pointcloud rather than by id so they survive
 * pointcloud reuse without growing forever — they're disposed with the state.
 */
export interface HQPipelineState extends RenderPipelineState {
  readonly kind: 'hq';
  depthMaterials: Map<unknown, RuntimePCMaterial>;
  attributeMaterials: Map<unknown, RuntimePCMaterial>;
  normalizationMaterial: NormalizationMaterial;
  normalizationEDLMaterial: NormalizationEDLMaterial;
  rtDepth: THREE.WebGLRenderTarget;
  rtAttribute: THREE.WebGLRenderTarget;
}

export class HQSplatRenderer implements RenderPipeline<HQPipelineState> {
  readonly kind = 'hq' as const;
  /** Whether to apply the EDL post-process during the normalization pass. */
  useEDL: boolean;

  constructor() {
    this.useEDL = false;
  }

  createState(): HQPipelineState {
    const normalizationMaterial = new NormalizationMaterial();
    normalizationMaterial.depthTest = true;
    normalizationMaterial.depthWrite = true;
    normalizationMaterial.transparent = true;

    const normalizationEDLMaterial = new NormalizationEDLMaterial();
    normalizationEDLMaterial.depthTest = true;
    normalizationEDLMaterial.depthWrite = true;
    normalizationEDLMaterial.transparent = true;

    const rtDepth = new THREE.WebGLRenderTarget(1024, 1024, {
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      format: THREE.RGBAFormat,
      type: THREE.FloatType,
      depthTexture: new THREE.DepthTexture(undefined as unknown as number, undefined as unknown as number, THREE.UnsignedIntType),
    });

    const rtAttribute = new THREE.WebGLRenderTarget(1024, 1024, {
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      format: THREE.RGBAFormat,
      type: THREE.FloatType,
      depthTexture: rtDepth.depthTexture,
    });

    return {
      kind: 'hq',
      depthMaterials: new Map(),
      attributeMaterials: new Map(),
      normalizationMaterial,
      normalizationEDLMaterial,
      rtDepth,
      rtAttribute,
    };
  }

  disposeState(state: HQPipelineState): void {
    state.normalizationMaterial.dispose();
    state.normalizationEDLMaterial.dispose();
    state.rtDepth.dispose();
    state.rtAttribute.dispose();
    for (const m of state.depthMaterials.values()) (m as unknown as THREE.Material).dispose();
    for (const m of state.attributeMaterials.values()) (m as unknown as THREE.Material).dispose();
    state.depthMaterials.clear();
    state.attributeMaterials.clear();
  }

  clear(ctx: RenderPipelineContext, state: HQPipelineState): void {
    const { threeRenderer, viewer } = ctx;
    const background = viewer.background;
    if (background === 'black') {
      threeRenderer.setClearColor(0x000000, 1);
    } else if (background === 'white') {
      threeRenderer.setClearColor(0xffffff, 1);
    } else {
      threeRenderer.setClearColor(0x000000, 0);
    }
    threeRenderer.clear();

    const oldTarget = threeRenderer.getRenderTarget();
    threeRenderer.setClearColor(0x000000, 0);
    threeRenderer.setRenderTarget(state.rtDepth);
    threeRenderer.clear(true, true, true);
    threeRenderer.setRenderTarget(state.rtAttribute);
    threeRenderer.clear(true, true, true);
    threeRenderer.setRenderTarget(oldTarget);
  }

  render(ctx: RenderPipelineContext, state: HQPipelineState): void {
    const { viewer, threeRenderer, pRenderer, camera, width, height, clipSpheres, overlays } = ctx;

    viewer.dispatchEvent({ type: 'render.pass.begin', viewer });

    state.rtDepth.setSize(width, height);
    state.rtAttribute.setSize(width, height);

    const visiblePointClouds = viewer.scene.pointclouds.filter((pc) => pc.visible);
    const originalMaterials = new Map<(typeof visiblePointClouds)[number], RuntimePCMaterial>();

    for (const pointcloud of visiblePointClouds) {
      originalMaterials.set(pointcloud, pointcloud.material as unknown as RuntimePCMaterial);

      if (!state.attributeMaterials.has(pointcloud)) {
        state.attributeMaterials.set(pointcloud, new PointCloudMaterial() as unknown as RuntimePCMaterial);
      }
      if (!state.depthMaterials.has(pointcloud)) {
        const depthMaterial = new PointCloudMaterial() as unknown as RuntimePCMaterial;
        depthMaterial.setDefine('depth_pass', '#define hq_depth_pass');
        depthMaterial.setDefine('use_edl', '#define use_edl');
        state.depthMaterials.set(pointcloud, depthMaterial);
      }
    }

    // DEPTH PASS
    for (const pointcloud of visiblePointClouds) {
      const octreeSize = pointcloud.pcoGeometry.boundingBox.getSize(new THREE.Vector3()).x;
      const material = originalMaterials.get(pointcloud);
      const depthMaterial = state.depthMaterials.get(pointcloud);
      if (!material || !depthMaterial) continue;

      depthMaterial.size = material.size;
      depthMaterial.minSize = material.minSize;
      depthMaterial.maxSize = material.maxSize;
      depthMaterial.pointSizeType = material.pointSizeType;
      depthMaterial.visibleNodesTexture = material.visibleNodesTexture;
      depthMaterial.weighted = false;
      depthMaterial.screenWidth = width;
      depthMaterial.shape = PointShape.CIRCLE;
      depthMaterial.screenHeight = height;
      depthMaterial.uniforms.visibleNodes.value = material.visibleNodesTexture;
      depthMaterial.uniforms.octreeSize.value = octreeSize;
      depthMaterial.spacing = pointcloud.pcoGeometry.spacing;
      depthMaterial.classification = material.classification;
      (depthMaterial.uniforms.classificationLUT.value as { image: { data: Uint8Array } }).image.data = (material.uniforms.classificationLUT.value as { image: { data: Uint8Array } }).image.data;
      depthMaterial.classificationTexture.needsUpdate = true;

      depthMaterial.uniforms.uFilterReturnNumberRange.value = material.uniforms.uFilterReturnNumberRange.value;
      depthMaterial.uniforms.uFilterNumberOfReturnsRange.value = material.uniforms.uFilterNumberOfReturnsRange.value;
      depthMaterial.uniforms.uFilterGPSTimeClipRange.value = material.uniforms.uFilterGPSTimeClipRange.value;
      depthMaterial.uniforms.uFilterPointSourceIDClipRange.value = material.uniforms.uFilterPointSourceIDClipRange.value;

      depthMaterial.setClipBoxes(material.clipBoxes);
      depthMaterial.setClipPolygons(material.clipPolygons);

      (pointcloud as unknown as { material: RuntimePCMaterial }).material = depthMaterial;
    }

    pRenderer.render(viewer.scene.scenePointCloud, camera, state.rtDepth, {
      clipSpheres,
      orderedIdentifiers: viewer.scene.orderedIdentifiers || [],
    });

    {
      // ATTRIBUTE PASS
      for (const pointcloud of visiblePointClouds) {
        const octreeSize = pointcloud.pcoGeometry.boundingBox.getSize(new THREE.Vector3()).x;
        const material = originalMaterials.get(pointcloud);
        const attributeMaterial = state.attributeMaterials.get(pointcloud);
        if (!material || !attributeMaterial) continue;

        attributeMaterial.size = material.size;
        attributeMaterial.minSize = material.minSize;
        attributeMaterial.maxSize = material.maxSize;
        attributeMaterial.pointSizeType = material.pointSizeType;
        attributeMaterial.activeAttributeName = material.activeAttributeName;
        attributeMaterial.visibleNodesTexture = material.visibleNodesTexture;
        attributeMaterial.weighted = true;
        attributeMaterial.screenWidth = width;
        attributeMaterial.screenHeight = height;
        attributeMaterial.shape = PointShape.CIRCLE;
        attributeMaterial.uniforms.visibleNodes.value = material.visibleNodesTexture;
        attributeMaterial.uniforms.octreeSize.value = octreeSize;
        attributeMaterial.spacing = pointcloud.pcoGeometry.spacing;
        attributeMaterial.classification = material.classification;
        (attributeMaterial.uniforms.classificationLUT.value as { image: { data: Uint8Array } }).image.data = (material.uniforms.classificationLUT.value as { image: { data: Uint8Array } }).image.data;
        attributeMaterial.classificationTexture.needsUpdate = true;

        attributeMaterial.uniforms.uFilterReturnNumberRange.value = material.uniforms.uFilterReturnNumberRange.value;
        attributeMaterial.uniforms.uFilterNumberOfReturnsRange.value = material.uniforms.uFilterNumberOfReturnsRange.value;
        attributeMaterial.uniforms.uFilterGPSTimeClipRange.value = material.uniforms.uFilterGPSTimeClipRange.value;
        attributeMaterial.uniforms.uFilterPointSourceIDClipRange.value = material.uniforms.uFilterPointSourceIDClipRange.value;

        attributeMaterial.elevationGradientRepat = material.elevationGradientRepat;
        attributeMaterial.elevationRange = material.elevationRange;
        attributeMaterial.gradient = material.gradient;
        attributeMaterial.matcap = material.matcap;

        attributeMaterial.intensityRange = material.intensityRange;
        attributeMaterial.intensityGamma = material.intensityGamma;
        attributeMaterial.intensityContrast = material.intensityContrast;
        attributeMaterial.intensityBrightness = material.intensityBrightness;

        attributeMaterial.rgbGamma = material.rgbGamma;
        attributeMaterial.rgbContrast = material.rgbContrast;
        attributeMaterial.rgbBrightness = material.rgbBrightness;

        attributeMaterial.weightRGB = material.weightRGB;
        attributeMaterial.weightIntensity = material.weightIntensity;
        attributeMaterial.weightElevation = material.weightElevation;
        attributeMaterial.weightClassification = material.weightClassification;
        attributeMaterial.weightReturnNumber = material.weightReturnNumber;
        attributeMaterial.weightSourceID = material.weightSourceID;

        attributeMaterial.color = material.color;

        attributeMaterial.setClipBoxes(material.clipBoxes);
        attributeMaterial.setClipPolygons(material.clipPolygons);

        (pointcloud as unknown as { material: RuntimePCMaterial }).material = attributeMaterial;
      }

      const gl = threeRenderer.getContext();

      threeRenderer.setRenderTarget(null);
      pRenderer.render(viewer.scene.scenePointCloud, camera, state.rtAttribute, {
        clipSpheres,
        blendFunc: [gl.SRC_ALPHA, gl.ONE],
        depthWrite: false,
        orderedIdentifiers: viewer.scene.orderedIdentifiers || [],
      });
    }

    // Restore the original materials.
    for (const [pointcloud, material] of originalMaterials) {
      (pointcloud as unknown as { material: RuntimePCMaterial }).material = material;
    }

    threeRenderer.setRenderTarget(null);
    if (viewer.background === 'skybox' && viewer.skybox) {
      threeRenderer.setClearColor(0x000000, 0);
      threeRenderer.clear();
      viewer.skybox.camera.rotation.copy(viewer.scene.cameraP.rotation);
      viewer.skybox.camera.fov = viewer.scene.cameraP.fov;
      viewer.skybox.camera.aspect = viewer.scene.cameraP.aspect;
      viewer.skybox.parent.rotation.x = 0;
      viewer.skybox.parent.updateMatrixWorld();
      viewer.skybox.camera.updateProjectionMatrix();
      threeRenderer.render(viewer.skybox.scene, viewer.skybox.camera);
    } else if (viewer.background === 'gradient') {
      threeRenderer.setClearColor(0x000000, 0);
      threeRenderer.clear();
      threeRenderer.render(viewer.scene.sceneBG, viewer.scene.cameraBG);
    } else if (viewer.background === 'black') {
      threeRenderer.setClearColor(0x000000, 1);
      threeRenderer.clear();
    } else if (viewer.background === 'white') {
      threeRenderer.setClearColor(0xffffff, 1);
      threeRenderer.clear();
    } else {
      threeRenderer.setClearColor(0x000000, 0);
      threeRenderer.clear();
    }

    {
      // NORMALIZATION PASS
      const normalizationMaterial = this.useEDL ? state.normalizationEDLMaterial : state.normalizationMaterial;

      if (this.useEDL) {
        normalizationMaterial.uniforms.edlStrength.value = viewer.edlStrength;
        normalizationMaterial.uniforms.radius.value = viewer.edlRadius;
        normalizationMaterial.uniforms.screenWidth.value = width;
        normalizationMaterial.uniforms.screenHeight.value = height;
        normalizationMaterial.uniforms.uEDLMap.value = state.rtDepth.texture;
      }

      normalizationMaterial.uniforms.uWeightMap.value = state.rtAttribute.texture;
      normalizationMaterial.uniforms.uDepthMap.value = state.rtAttribute.depthTexture;

      Utils.screenPass.render(threeRenderer, normalizationMaterial);
    }

    threeRenderer.render(viewer.scene.scene, camera);

    viewer.dispatchEvent({ type: 'render.pass.scene', viewer });

    threeRenderer.clearDepth();

    viewer.dispatchEvent({ type: 'render.pass.perspective_overlay', viewer });

    if (overlays.controls && viewer.controls.sceneControls) threeRenderer.render(viewer.controls.sceneControls, camera);
    if (overlays.clippingVolumeMesh) threeRenderer.render(viewer.clippingTool.sceneVolume, camera);
    if (overlays.toolOverlay) {
      for (const toolScene of viewer.toolOverlayScenes) threeRenderer.render(toolScene, camera);
    }

    // Top-most UI overlay: clear depth first so its contents always draw over
    // every other scene.
    if (viewer.uiScene) {
      threeRenderer.clearDepth();
      threeRenderer.render(viewer.uiScene, camera);
    }

    // Transform gizmos render LAST (before the nav cube's viewport switch) —
    // the manipulation widget must never be buried under glyphs/overlays.
    if (overlays.gizmo && viewer.gizmoScene) {
      threeRenderer.clearDepth();
      threeRenderer.render(viewer.gizmoScene, camera);
    }

    if (overlays.controls) {
      // NavigationCube — sized in screen pixels, drawn over a corner of the canvas.
      threeRenderer.setViewport(width - viewer.navigationCube.width, height - viewer.navigationCube.width, viewer.navigationCube.width, viewer.navigationCube.width);
      threeRenderer.render(viewer.navigationCube as unknown as THREE.Object3D, viewer.navigationCube.camera);
      threeRenderer.setViewport(0, 0, width, height);
    }

    viewer.dispatchEvent({ type: 'render.pass.end', viewer });
  }
}
